import React from 'react';
import '../../styles/design-tokens.css';
import '../../styles/owner-ui.css';
import '../../styles/auth-layout.css';
import { BUILD_SHA, BUILD_TIME } from '../../config/buildInfo.js';

/**
 * AuthLayout - Unauthenticated layout for login and password reset pages
 * 
 * Provides a clean, centered layout without any authenticated navigation,
 * property switcher, or account menu. Only displays branding and content.
 */
export default function AuthLayout({ children }) {
  return (
    <div className="auth-layout" style={styles.container}>
      {/* Minimal header with branding only */}
      <header style={styles.header}>
        <div style={styles.brand}>
          <img 
            src="/brand/clc/logos/clc-logo-blue-768x768-png-cea5c7b9.webp" 
            alt="CLC Vacation Homes" 
            style={styles.logo}
          />
          <div style={styles.brandText}>
            <span style={styles.brandName}>CLC</span>
            <span style={styles.brandDivider}>/</span>
            <span style={styles.brandContext}>Owner Portal</span>
          </div>
        </div>
      </header>

      {/* Main content area - centered */}
      <main style={styles.main}>
        {children}
      </main>

      {/* Footer - consistent with desktop OwnerLayout */}
      <footer style={styles.footer}>
        <p style={styles.footerText}>
          © 2026 CLC Vacation Homes: Owner's Portal
          <span style={styles.buildStamp}>
            Build: {BUILD_SHA} {BUILD_TIME}
          </span>
        </p>
      </footer>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    // Height/overflow in auth-layout.css for 100dvh + 100vh fallback
    background: 'linear-gradient(180deg, var(--color-bg-secondary, #f9fafb) 0%, var(--color-bg-tertiary, #eceef0) 100%)',
  },
  header: {
    flexShrink: 0,
    minHeight: '48px',
    padding: 'var(--spacing-2) var(--spacing-4)',
    paddingTop: 'max(var(--spacing-2), env(safe-area-inset-top))',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'var(--color-header-bg-brand, #254860)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    boxSizing: 'border-box',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-header-text-brand, #FFFFFF)',
    letterSpacing: '-0.02em',
  },
  logo: {
    height: '24px',
    width: 'auto',
    maxHeight: '24px',
    objectFit: 'contain',
    filter: 'brightness(0) invert(1)', // White logo for dark header
  },
  brandText: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)',
  },
  brandName: {
    fontWeight: 'var(--font-weight-bold)',
    fontSize: 'var(--font-size-base)',
    letterSpacing: '-0.03em',
  },
  brandDivider: {
    color: 'rgba(255, 255, 255, 0.6)',
  },
  brandContext: {
    fontWeight: 'var(--font-weight-semibold)',
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 'var(--font-size-sm)',
    letterSpacing: '-0.01em',
  },
  main: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 'var(--spacing-4)',
    paddingLeft: 'var(--spacing-4)',
    paddingRight: 'var(--spacing-4)',
    paddingBottom: 'max(var(--spacing-4), env(safe-area-inset-bottom))',
    boxSizing: 'border-box',
  },
  footer: {
    flexShrink: 0,
    padding: 'var(--spacing-2) var(--spacing-4)',
    paddingBottom: 'max(var(--spacing-2), env(safe-area-inset-bottom))',
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    borderTop: '1px solid var(--color-border-light, #e8e8ea)',
    textAlign: 'center',
  },
  footerText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    margin: 0,
    fontWeight: 'var(--font-weight-normal)',
    letterSpacing: '0.01em',
  },
  buildStamp: {
    marginLeft: 'var(--spacing-2)',
    fontFamily: 'monospace',
    fontSize: '0.85em',
    opacity: 0.9,
  },
};
